import React from "react";
import { Box, BoxProps } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { tokens } from "../../theme";

export type SurfaceTone =
  | "neutral"
  | "positive"
  | "negative"
  | "warning"
  | "info"
  | "brand"
  | "accent";

// One card scale app-wide: 20px padding on phones, 24px from tablets up;
// 24px radius on phones, 28px from tablets up (nested panels 16, tiles 12).
const PADDING = {
  none: 0,
  sm: { xs: 1.5, md: 2 },
  md: { xs: 2.5, md: 3 },
  lg: { xs: 2.5, md: 4 },
} as const;

const CARD_RADIUS = { xs: "24px", md: "28px" } as const;
const RADIUS = {
  md: CARD_RADIUS,
  lg: CARD_RADIUS,
  xl: CARD_RADIUS,
} as const;

export interface SurfaceCardProps extends Omit<BoxProps, "padding"> {
  /** Colors the soft top glow; neutral cards have none. */
  tone?: SurfaceTone;
  /** Custom glow color (e.g. chain accent); overrides `tone`. */
  tint?: string;
  padding?: keyof typeof PADDING;
  radius?: keyof typeof RADIUS;
  /** Hover lift + pointer for clickable cards. */
  interactive?: boolean;
  /** Recessed panel inside a card (dark, no blur). */
  inset?: boolean;
  /** Brighter glass for hero surfaces. */
  strong?: boolean;
}

/**
 * Glass surface: translucent gradient, hairline edge with a top highlight,
 * backdrop blur on larger screens and an optional tinted glow from the top.
 */
export const SurfaceCard = React.forwardRef<HTMLDivElement, SurfaceCardProps>(
  (
    {
      tone = "neutral",
      tint,
      padding = "md",
      radius = "lg",
      interactive = false,
      inset = false,
      strong = false,
      sx,
      children,
      ...rest
    },
    ref
  ) => {
    const theme = useTheme();
    const toneColor: Record<SurfaceTone, string | null> = {
      neutral: null,
      positive: theme.palette.success.main,
      negative: theme.palette.error.main,
      warning: theme.palette.warning.main,
      info: theme.palette.info.main,
      brand: theme.palette.secondary.main,
      accent: "#7B8CFF",
    };
    const glow = tint ?? toneColor[tone];

    const base = inset
      ? {
          background: tokens.glass.inset,
          border: "1px solid rgba(255,255,255,0.07)",
          borderRadius: "16px",
        }
      : {
          background: strong ? tokens.glass.bgStrong : tokens.glass.bg,
          border: `1px solid ${tokens.glass.border}`,
          boxShadow: `${tokens.glass.highlight}, ${tokens.glass.shadow}`,
          borderRadius: RADIUS[radius],
          backdropFilter: { xs: "none", md: tokens.glass.blur },
          WebkitBackdropFilter: { xs: "none", md: tokens.glass.blur },
        };

    return (
      <Box
        ref={ref}
        sx={[
          {
            position: "relative",
            isolation: "isolate",
            overflow: "hidden",
            // `clip` clips the glow without creating a scroll container, so sticky children keep working.
            "@supports (overflow: clip)": { overflow: "clip" },
            minWidth: 0,
            p: PADDING[padding],
            ...base,
          },
          !!glow &&
            !inset && {
              "&::before": {
                content: '""',
                position: "absolute",
                inset: "-1px -1px auto -1px",
                height: 150,
                background: `radial-gradient(80% 100% at 25% 0%, ${alpha(glow, 0.24)} 0%, ${alpha(glow, 0)} 70%)`,
                zIndex: -1,
                pointerEvents: "none",
              },
            },
          interactive && {
            cursor: "pointer",
            transition: "transform 200ms ease, border-color 200ms ease, box-shadow 200ms ease",
            "@media (hover: hover)": {
              "&:hover": {
                transform: "translateY(-3px)",
                borderColor: "rgba(255,255,255,0.2)",
                boxShadow: `${tokens.glass.highlight}, 0 36px 70px -30px rgba(0,0,0,0.95)${
                  glow ? `, 0 0 0 1px ${alpha(glow, 0.15)}` : ""
                }`,
              },
            },
            "&:active": { transform: "translateY(0)" },
            "&:focus-visible": {
              outline: `2px solid ${alpha(theme.palette.secondary.main, 0.8)}`,
              outlineOffset: 2,
            },
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...rest}
      >
        {children}
      </Box>
    );
  }
);

SurfaceCard.displayName = "SurfaceCard";

export default SurfaceCard;
